/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button } from "@nordcloud/gnui";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { CloudAccount } from "~/models";
import { ACTIONS, Can } from "~/routing";

type Props = {
  addAccount: (newAccount: CloudAccount) => Promise<void>;
};

function CloudAccountAdd({ addAccount }: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const methods = useForm();
  const watcher = methods.watch();

  const onSubmit = async (values: CloudAccount) => {
    setIsLoading(true);
    try {
      await addAccount(values);
    } finally {
      setIsLoading(false);
      setIsSidebarOpen(false);
    }
  };

  return (
    <>
      <Can action={ACTIONS.EDIT_APPLICATION}>
        <Button
          size="md"
          css={{ marginLeft: "auto", marginBottom: "1rem" }}
          icon="plus"
          onClick={() => setIsSidebarOpen(true)}
        >
          Add account
        </Button>
      </Can>

      <SidebarForm
        title="Add cloud account"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarTextField name="accountId" label="Account ID" required />
        <SidebarTextField name="accountName" label="Account name" required />
        <SidebarDropdown
          name="cloudProvider"
          label="Cloud provider"
          options={["AWS", "Azure", "Google Cloud", "IBM Cloud"]}
          required
        />

        <SidebarFormButtons
          submitText="Add"
          submitEnabled={
            !!(
              watcher.accountId &&
              watcher.accountName &&
              watcher.cloudProvider
            )
          }
          submitLoading={isLoading}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { CloudAccountAdd };
